import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksDialog } from '../../components/liks/LiksDialog/LiksDialog';
import type { LacCreateUserInput } from '../../types/lac';
import { CreateLacUserDialogOptionalFields } from './CreateLacUserDialogOptionalFields';
import { CreateLacUserDialogOrganisationFields } from './CreateLacUserDialogOrganisationFields';
import { CreateLacUserDialogRequiredFields } from './CreateLacUserDialogRequiredFields';
import { useCreateLacUserDialogForm } from './useCreateLacUserDialogForm';
import './CreateLacUserDialog.css';

type CreateLacUserDialogProps = {
  onCreate: (input: LacCreateUserInput) => void;
  onClose: () => void;
  isSubmitting?: boolean;
  /** When set, organisation is locked to this tenant (no picker / create-org). */
  fixedTenantName?: string;
  fixedTenantDisplayName?: string;
};

export function CreateLacUserDialog({
  onCreate,
  onClose,
  isSubmitting = false,
  fixedTenantName,
  fixedTenantDisplayName,
}: CreateLacUserDialogProps) {
  const form = useCreateLacUserDialogForm({
    onCreate,
    isSubmitting,
    fixedTenantName,
    fixedTenantDisplayName,
  });

  return (
    <LiksDialog
      isOpen
      as="form"
      size="md"
      title={form.translate('lac.users.create.title')}
      onClose={onClose}
      onSubmit={form.handleSubmit}
      closeLabel={form.translate('lac.users.create.close')}
      closeDisabled={isSubmitting}
      isDismissDisabled={isSubmitting}
      footer={
        <>
          <LiksButton
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {form.translate('lac.users.create.cancel')}
          </LiksButton>
          <LiksButton type="submit" disabled={form.isCreateDisabled}>
            {form.translate('lac.users.create.submit')}
          </LiksButton>
        </>
      }
    >
      <CreateLacUserDialogOrganisationFields
        tenantFieldId={form.tenantFieldId}
        roleFieldId={form.roleFieldId}
        isTenantFixed={form.isTenantFixed}
        fixedTenantDisplayValue={form.fixedTenantDisplayValue}
        isTenantsLoading={form.tenantsQuery.isLoading}
        isTenantsError={form.tenantsQuery.isError}
        tenantsLength={form.tenants.length}
        isCreatingNewTenant={form.isCreatingNewTenant}
        tenantSelectValue={form.tenantSelectValue}
        tenantOptions={form.tenantOptions}
        newTenantName={form.newTenantName}
        onNewTenantNameChange={form.setNewTenantName}
        onTenantChange={form.handleTenantChange}
        isDuplicateTenantName={form.isDuplicateTenantName}
        role={form.role}
        roleOptions={form.roleOptions}
        onRoleChange={form.handleRoleChange}
        isSubmitting={isSubmitting}
      />

      <CreateLacUserDialogRequiredFields
        email={form.email}
        onEmailChange={form.handleEmailChange}
        isEmailInvalid={form.isEmailInvalid}
        username={form.username}
        onUsernameChange={form.handleUsernameChange}
        isUsernameInvalid={form.isUsernameInvalid}
        firstName={form.firstName}
        onFirstNameChange={form.setFirstName}
        lastName={form.lastName}
        onLastNameChange={form.setLastName}
        sendInviteEmailFieldId={form.sendInviteEmailFieldId}
        sendInviteEmail={form.sendInviteEmail}
        onSendInviteEmailChange={form.setSendInviteEmail}
        emailAutoFocus={
          form.isTenantFixed ||
          (form.tenants.length > 0 && !form.isCreatingNewTenant)
        }
        isSubmitting={isSubmitting}
      />

      <CreateLacUserDialogOptionalFields
        optionalFieldsId={form.optionalFieldsId}
        showOptionalFields={form.showOptionalFields}
        onToggleOptionalFields={() =>
          form.setShowOptionalFields((prev) => !prev)
        }
        validFrom={form.validFrom}
        onValidFromChange={form.setValidFrom}
        validTo={form.validTo}
        onValidToChange={form.setValidTo}
        isValidToBeforeValidFrom={form.isValidToBeforeValidFrom}
        isSubmitting={isSubmitting}
      />
    </LiksDialog>
  );
}
